/**
 * /payroll-l2/flagged — Flagged labor events awaiting review.
 * Focuses only on events with verification_status = "flagged".
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

import { api } from "@/lib/api";
import type { LaborEvent } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/flagged")({
  component: FlaggedEventsPage,
});

function fmtDt(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function FlaggedEventsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "labor-events", "flagged"],
    queryFn: async () => {
      return await api<LaborEvent[]>("/api/payroll-l2/labor-events?status=flagged&limit=200");
    },
  });

  const bulkOverrideMut = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(
        ids.map((id) =>
          api(`/api/payroll-l2/labor-events/${id}/override`, {
            method: "POST",
            body: { reason: "Batch override by reviewer" },
          }),
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2"] });
      setSelected(new Set());
    },
  });

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(data?.map((e) => e.id) ?? []));
  const clearAll = () => setSelected(new Set());

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <FlagIcon color="warning" />
          <Typography variant="h5" fontWeight={600}>Flagged Events</Typography>
          {data && <Chip label={`${data.length} flagged`} color="warning" size="small" />}
        </Stack>
        <HelpHint docPath="payroll-l2/anomalies.md" label="Flagged events help" />
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load flagged events.</Alert>}

      {data && data.length === 0 && (
        <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
          No flagged events — all clear.
        </Alert>
      )}

      {data && data.length > 0 && (
        <>
          <Stack direction="row" gap={1} mb={1}>
            <Button size="small" onClick={selectAll}>Select all</Button>
            <Button size="small" onClick={clearAll}>Clear</Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              disabled={selected.size === 0 || bulkOverrideMut.isPending}
              onClick={() => bulkOverrideMut.mutate([...selected])}
            >
              Override selected ({selected.size})
            </Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Employee</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Flag Reasons</TableCell>
                <TableCell>Face Conf.</TableCell>
                <TableCell>Geo (m)</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((ev) => (
                <TableRow key={ev.id} hover selected={selected.has(ev.id)}>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selected.has(ev.id)}
                      onChange={() => toggleSelect(ev.id)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{ev.employee_id.slice(0, 8)}</TableCell>
                  <TableCell>{fmtDt(ev.clock_in)}</TableCell>
                  <TableCell>{ev.worked_hours ?? "—"}</TableCell>
                  <TableCell>
                    {ev.flag_reasons?.map((f) => (
                      <Chip key={f} label={f} size="small" color="warning" variant="outlined" sx={{ mr: 0.5, mb: 0.5, fontSize: 10 }} />
                    ))}
                  </TableCell>
                  <TableCell>{ev.face_match_confidence ? `${(Number(ev.face_match_confidence) * 100).toFixed(0)}%` : "—"}</TableCell>
                  <TableCell>{ev.geo_distance_meters ?? "—"}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<CancelOutlinedIcon />}
                      color="error"
                      onClick={() =>
                        api(`/api/payroll-l2/labor-events/${ev.id}/reject`, {
                          method: "POST",
                          body: { reason: "Rejected by reviewer" },
                        }).then(() => qc.invalidateQueries({ queryKey: ["payroll-l2"] }))
                      }
                    >
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}
